import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ToastSeverity, useFetcher, useToast } from 'thermia-react-container';
import { config } from '../config';
import { FeedbackEnum, GuideType, TroubleshootTagData } from '../types/types';

const useTroubleshootService = () => {
  const { fetcher } = useFetcher();

  const { t } = useTranslation('troubleshoot');
  const { addToast } = useToast();

  const getHeaders = () => {
    return {
      'Accept-Language': i18n.language
    };
  };

  const fetchTagdata = async (type: GuideType) => {
    try {
      const headers = getHeaders();
      const url = `${config.apiUrl}/troubleshoot/tags?type=${type}`;
      const response = await fetcher<TroubleshootTagData>('GET', url, headers);
      return response;
    } catch (error) {
      addToast(t('troubleshoot.errors.unableToFetchTags'), ToastSeverity.ERROR);
      throw error;
    }
  };

  const searchGuides = async (tagIds: number[], skip: number, searchTerm: string, take: number, type: GuideType) => {
    const headers = getHeaders();

    const tags = tagIds.map((tag) => `&tags=${tag}`).join('');
    const textInCall = searchTerm ? `text=${searchTerm}` : '';
    try {
      const url = `${config.apiUrl}/troubleshoot/search?type=${type}&${textInCall}&skip=${skip}&take=${take}${tags}`;
      const response = await fetcher('GET', url, headers);
      return response;
    } catch (error) {
      addToast(t('troubleshoot.errors.unableToFetchGuides'), ToastSeverity.ERROR);
      return null;
    }
  };

  const getGuideById = async (id: number | string) => {
    try {
      const headers = getHeaders();
      const url = `${config.apiUrl}/troubleshoot/guide/${id}`;
      const response = await fetcher('GET', url, headers);
      if (response) {
        return response;
      }
    } catch (error) {
      addToast(t('troubleshoot.errors.unableToFetchGuide'), ToastSeverity.ERROR);
      throw error;
    }
  };

  const postFeedback = async (id: number | string, feedback: FeedbackEnum) => {
    try {
      const headers = getHeaders();
      const url = `${config.apiUrl}/troubleshoot/guide/${id}/feedback?feedback=${feedback}`;
      const response = await fetcher<{ feedback: FeedbackEnum }>('POST', url, headers);
      return response.feedback;
    } catch (error) {
      addToast(t('troubleshoot.errors.postFeedback'), ToastSeverity.ERROR);
      throw error;
    }
  };

  const postComment = async (id: number | string, comment: string, anonymous: boolean) => {
    try {
      const headers = getHeaders();
      const url = `${config.apiUrl}/troubleshoot/guide/${id}/comment?anonymous=${anonymous}`;
      const body = {
        comment
      };
      return await fetcher('POST', url, headers, body);
    } catch (error) {
      addToast(t('troubleshoot.errors.postComment'), ToastSeverity.ERROR);
      throw error;
    }
  };

  return {
    fetchTagdata,
    searchGuides,
    getGuideById,
    postFeedback,
    postComment
  };
};

export default useTroubleshootService;
